import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "review_engagements";

// Helper functions for localStorage operations
const getStoredEngagements = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading engagements from localStorage:", error);
    return {};
  }
};

const setStoredEngagements = (engagements) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(engagements));
  } catch (error) {
    console.error("Error saving engagements to localStorage:", error);
  }
};

export const useEngagementCache = () => {
  const [engagements, setEngagements] = useState({});

  // Load engagements from localStorage on mount
  useEffect(() => {
    const storedEngagements = getStoredEngagements();
    setEngagements(storedEngagements);
  }, []);

  // Get engagement state for a specific review
  const getEngagement = useCallback(
    (reviewId) => {
      return (
        engagements[reviewId] || {
          like: false,
          dislike: false,
          reported: false,
        }
      );
    },
    [engagements]
  );

  // Update engagement for a specific review
  const updateEngagement = useCallback((reviewId, type) => {
    setEngagements((prevEngagements) => {
      const currentEngagement = prevEngagements[reviewId] || {
        like: false,
        dislike: false,
        reported: false,
      };

      let updatedEngagement = { ...currentEngagement };

      if (type === "like") {
        // Toggle like, and if we're liking, remove dislike
        updatedEngagement.like = !currentEngagement.like;
        if (updatedEngagement.like) {
          updatedEngagement.dislike = false;
        }
      } else if (type === "dislike") {
        // Toggle dislike, and if we're disliking, remove like
        updatedEngagement.dislike = !currentEngagement.dislike;
        if (updatedEngagement.dislike) {
          updatedEngagement.like = false;
        }
      } else if (type === "report") {
        // Toggle report
        updatedEngagement.reported = !currentEngagement.reported;
      }

      const newEngagements = {
        ...prevEngagements,
        [reviewId]: updatedEngagement,
      };

      // Save to localStorage
      setStoredEngagements(newEngagements);

      return newEngagements;
    });
  }, []);

  // Clear all engagements (useful for logout or reset)
  const clearEngagements = useCallback(() => {
    setEngagements({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Clear engagement for a specific review
  const clearEngagement = useCallback((reviewId) => {
    setEngagements((prevEngagements) => {
      const newEngagements = { ...prevEngagements };
      delete newEngagements[reviewId];
      setStoredEngagements(newEngagements);
      return newEngagements;
    });
  }, []);

  return {
    engagements,
    getEngagement,
    updateEngagement,
    clearEngagements,
    clearEngagement,
  };
};